import type { AppRole } from '../../utils/auth';
import type { TourChapterDef, TourFlowMode, TourRolePrefix } from '../types';
import { employeeChapters } from './employee';
import { managerChapters } from './manager';
import { ownerChapters } from './owner';

export function rolePrefix(role: AppRole | string | null | undefined): TourRolePrefix | null {
  if (role === 'OWNER') return 'owner';
  if (role === 'MANAGER') return 'manager';
  if (role === 'EMPLOYEE') return 'employee';
  return null;
}

export function flowKey(prefix: TourRolePrefix) {
  return `${prefix}.flow`;
}

export function getChapterSequence(role: AppRole | string | null | undefined): TourChapterDef[] {
  const p = rolePrefix(role);
  if (p === 'owner') return ownerChapters;
  if (p === 'manager') return managerChapters;
  if (p === 'employee') return employeeChapters;
  return [];
}

export function getChapterById(
  role: AppRole | string | null | undefined,
  id: string
): TourChapterDef | null {
  return getChapterSequence(role).find((c) => c.id === id) || null;
}

/** Match chapter for current path (first matching route prefix). */
export function chapterForPath(
  role: AppRole | string | null | undefined,
  pathname: string,
  search = ''
): TourChapterDef | null {
  const full = `${pathname}${search}`;
  const seq = getChapterSequence(role);
  // Prefer exact / longer routes first
  const sorted = [...seq].sort((a, b) => b.route.length - a.route.length);
  for (const ch of sorted) {
    const [path, qs] = ch.route.split('?');
    if (pathname !== path && !pathname.startsWith(path + '/')) continue;
    if (qs && !search.includes(qs) && !full.includes(qs)) {
      // settings?tab=billing vs settings - allow base settings chapter without tab
      if (path === '/settings' && ch.id.endsWith('.settings')) return ch;
      continue;
    }
    return ch;
  }
  return null;
}

export function nextChapter(
  role: AppRole | string | null | undefined,
  currentId: string
): TourChapterDef | null {
  const seq = getChapterSequence(role);
  const idx = seq.findIndex((c) => c.id === currentId);
  if (idx < 0 || idx >= seq.length - 1) return null;
  return seq[idx + 1];
}

export function firstPendingChapter(
  role: AppRole | string | null | undefined,
  progress: Record<string, { status?: string; mode?: string } | undefined>
): TourChapterDef | null {
  const seq = getChapterSequence(role);
  for (const ch of seq) {
    const e = progress[ch.id];
    if (!e || (e.status !== 'completed' && e.status !== 'skipped')) return ch;
  }
  return null;
}

export function parseFlowMode(
  progress: Record<string, { status?: string; mode?: string } | undefined>,
  prefix: TourRolePrefix
): TourFlowMode | null {
  const e = progress[flowKey(prefix)];
  if (!e) return null;
  if (e.mode === 'guided' || e.mode === 'discover' || e.mode === 'done') return e.mode;
  if (e.status === 'completed' || e.status === 'skipped') return 'done';
  return null;
}
